import React, { useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Travica, ArrowRightThin, CloseX } from '../components/vd/LineArt';
import { BlobDecor, WaveDivider } from '../components/vd/Decor';
import { useReveal } from '../components/vd/useReveal';
import { IconImg, HomeIcons } from '../components/vd/IconImg';
import { BrochureForm } from '../components/BrochurePopup';

const NEEDS = [
  { need: 'Želim bolje razumeti svoje telo.', expert: 'Anja Kikl', to: '/tvoje-telo', icon: HomeIcons.Dojencek },
  { need: 'Pripravljam se na porod.', expert: 'Špela Peternel', to: '/prvi-dnevi-po-porodu', icon: HomeIcons.Nosecnost },
  { need: 'Potrebujem pomoč pri dojenju.', expert: 'Amanda Golob', to: '/uciva-se-hranjenja', icon: HomeIcons.Steklenicka },
  { need: 'Po porodu želim ponovno zaupati svojemu telesu.', expert: 'Jasmina Krajnc', to: '/tvoje-telo', icon: HomeIcons.MamaZDojenckom },
  { need: 'Želim se ponovno povezati s sabo.', expert: 'Asja Samec', to: '/prvi-dnevi-po-porodu', icon: HomeIcons.SkodelicaSrce },
  { need: 'Želim okrepiti povezanost z dojenčkom.', expert: 'Špela Ferbežar Ban', to: '/povezanost', icon: '/icons/home_6_roke_na_trebuhu.svg' },
];

const TESTIMONIALS = [
  {
    lead: 'Nadaljujte s svojim poslanstvom, s tem date nekaj dobrega mamam…',
    full: 'Nadaljujte s svojim poslanstvom, s tem date nekaj dobrega mamam (kot sem sama po carskem rezu) in dodatno povezavo z otrokom (ker nisem imela takšne povezave, kot mame pri naravnem porodu).',
    author: 'mamica Tina',
  },
  {
    lead: 'Vsem mamicam toplo priporočam obisk tečaja, saj ima veliko pozitivnih lastnosti…',
    full: 'Vsem mamicam toplo priporočam obisk tečaja, saj ima veliko pozitivnih lastnosti, ki se odražajo tako na otroku kot na mamici. Znanje, ki sem ga pridobila, bom lahko uporabljala dnevno. Iskrena hvala inštruktorici za navdih, trud in čas, ki nam ga je namenila. Toplo priporočam.',
    author: 'mamica Daša',
  },
  {
    lead: 'čas, ki ga vložiš že od obdobja dojenčka pa potem naprej največja naložba…',
    full: 'Lepo, da se mame čedalje bolj zavedajo, da je čas, ki ga vložiš že od obdobja dojenčka pa potem naprej največja naložba in da se za inštruktorje odločijo tako srčni ljudje, kot je naša inštruktorica.',
    author: 'mamica Maša',
  },
];

const HomePage = () => {
  const navigate = useNavigate();
  const { openPopup } = useOutletContext() || {};
  const [openIdx, setOpenIdx] = useState(null);
  const [brochureExpanded, setBrochureExpanded] = useState(false);
  const needsRef = useReveal();
  const testimonialsRef = useReveal();

  return (
    <main data-testid="home-page" className="relative overflow-hidden">
      <BlobDecor className="-top-20 -right-24 opacity-70" size={380} color="rgba(233, 224, 205, 0.55)" />
      <BlobDecor className="top-[80vh] -left-32 opacity-55" size={340} color="rgba(213, 219, 205, 0.45)" variant="slower" />
      <BlobDecor className="top-[160vh] -right-40 opacity-50" size={420} color="rgba(238, 231, 225, 0.5)" />
      <Helmet>
        <title>Val Dotika – Prostor, kjer nisi sama</title>
        <meta name="description" content="Val Dotika je nastajajoč digitalni prostor celostne podpore za mamice pred zanositvijo, med nosečnostjo in po porodu." />
        <meta property="og:title" content="Val Dotika" />
        <meta property="og:description" content="Prostor, kjer mamica ni sama." />
      </Helmet>

      {/* =========================================
    10.1 VISION – MOBILE
    ========================================= */}
<section
  className="md:hidden relative overflow-hidden min-h-[650px] flex items-center justify-center px-4 pt-6 pb-10"
  data-testid="home-vision-mobile"
>
  {/* Nežen svetel oblak */}
  <div
    aria-hidden="true"
    className="absolute inset-0 pointer-events-none"
    style={{
      background:
        'radial-gradient(ellipse 85% 70% at 52% 48%, rgba(223,210,188,0.22) 0%, rgba(223,210,188,0.10) 52%, transparent 78%)',
    }}
  />

  {/* Mamica kot velika nežna ilustracija v ozadju */}
  <img
    src="/icons/vd_hero_mama_transparent.png"
    alt=""
    aria-hidden="true"
    className="
      absolute
      left-1/2
      top-[47%]
      -translate-x-1/2
      -translate-y-1/2
      w-[155vw]
      max-w-none
      h-auto
      opacity-[0.18]
      pointer-events-none
      select-none
      z-0
    "
    style={{
      WebkitMaskImage:
        'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.45) 10%, black 28%, black 72%, rgba(0,0,0,0.35) 90%, transparent 100%)',
      maskImage:
        'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.45) 10%, black 28%, black 72%, rgba(0,0,0,0.35) 90%, transparent 100%)',
    }}
  />

  {/* Oval + naslov */}
  <div className="relative z-10 w-full max-w-[370px] min-h-[300px] flex items-center justify-center px-7">

    <svg
      aria-hidden="true"
      viewBox="0 0 600 340"
      preserveAspectRatio="none"
      className="absolute inset-0 w-full h-full pointer-events-none"
    >
      <ellipse
        cx="300"
        cy="170"
        rx="278"
        ry="140"
        fill="none"
        stroke="rgba(105,94,75,0.40)"
        strokeWidth="1.3"
        transform="rotate(-2 300 170)"
      />

      <ellipse
        cx="292"
        cy="165"
        rx="266"
        ry="132"
        fill="none"
        stroke="rgba(105,94,75,0.30)"
        strokeWidth="1.05"
        transform="rotate(3 292 165)"
      />

      <ellipse
        cx="306"
        cy="174"
        rx="286"
        ry="146"
        fill="none"
        stroke="rgba(105,94,75,0.22)"
        strokeWidth="0.95"
        transform="rotate(-4 306 174)"
      />

      <ellipse
        cx="298"
        cy="168"
        rx="272"
        ry="136"
        fill="none"
        stroke="rgba(105,94,75,0.16)"
        strokeWidth="0.8"
        transform="rotate(5 298 168)"
      />
    </svg>

    <p className="relative z-10 max-w-[290px] font-serif italic text-vd-text text-[21px] leading-[1.5] text-center">
      Predstavljaj si, da bi bilo vse,
      <br />
      kar potrebuješ v prvih 40 dneh
      <br />
      po porodu, zbrano na enem
      <br />
      mestu.
    </p>

  </div>

  <div className="vd-organic-waves" aria-hidden="true" />
</section>


{/* =========================================
    10.1 VISION – TABLET / DESKTOP
    ========================================= */}
<section
  className="hidden md:flex relative overflow-hidden min-h-[620px] lg:min-h-[650px] items-center pb-8"
  data-testid="home-vision"
>
  {/* Mama z dojenčkom */}
  <div
    className="absolute right-[11%] lg:right-[15%] top-[49%] -translate-y-1/2 w-[43rem] lg:w-[50rem] pointer-events-none select-none z-0"
    data-testid="home-hero-decor"
  >
    <div
      aria-hidden="true"
      className="absolute inset-[10%] rounded-full"
      style={{
        background:
          'radial-gradient(ellipse at center, rgba(223,210,188,0.32) 0%, rgba(223,210,188,0.18) 45%, rgba(223,210,188,0.07) 68%, transparent 82%)',
        filter: 'blur(18px)',
        transform: 'scale(1.12)',
      }}
    />

    <img
      src="/icons/vd_hero_mama_transparent.png"
      alt=""
      aria-hidden="true"
      className="relative z-10 w-full h-auto opacity-[0.46]"
    />
  </div>

  <div className="relative z-10 w-full max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
    <div className="relative w-[600px] max-w-[48vw] min-h-[340px] flex items-center justify-center">

      <svg
        aria-hidden="true"
        viewBox="0 0 600 340"
        className="absolute inset-0 w-full h-full pointer-events-none"
      >
        <ellipse
          cx="300"
          cy="170"
          rx="278"
          ry="140"
          fill="none"
          stroke="rgba(105,94,75,0.40)"
          strokeWidth="1.3"
          transform="rotate(-2 300 170)"
        />

        <ellipse
          cx="292"
          cy="165"
          rx="266"
          ry="132"
          fill="none"
          stroke="rgba(105,94,75,0.30)"
          strokeWidth="1.05"
          transform="rotate(3 292 165)"
        />

        <ellipse
          cx="306"
          cy="174"
          rx="286"
          ry="146"
          fill="none"
          stroke="rgba(105,94,75,0.22)"
          strokeWidth="0.95"
          transform="rotate(-4 306 174)"
        />

        <ellipse
          cx="298"
          cy="168"
          rx="272"
          ry="136"
          fill="none"
          stroke="rgba(105,94,75,0.16)"
          strokeWidth="0.8"
          transform="rotate(5 298 168)"
        />

        <ellipse
          cx="304"
          cy="171"
          rx="281"
          ry="143"
          fill="none"
          stroke="rgba(105,94,75,0.12)"
          strokeWidth="0.7"
          transform="rotate(-6 304 171)"
        />
      </svg>

      <p className="relative z-10 font-serif italic text-vd-text text-[30px] lg:text-[34px] leading-[1.42] text-left">
        <span className="block whitespace-nowrap">
          Predstavljaj si, da bi bilo vse,
        </span>
        <span className="block whitespace-nowrap">
          kar potrebuješ v prvih 40 dneh
        </span>
        <span className="block whitespace-nowrap">
          po porodu, zbrano na enem
        </span>
        <span className="block whitespace-nowrap">
          mestu.
        </span>
      </p>

    </div>
  </div>

  <div className="vd-organic-waves" aria-hidden="true" />
</section>

      {/* 10.2 + 10.3 Question & Brochure */}
<section
  className="relative overflow-hidden min-h-[620px] lg:min-h-[660px] flex items-center py-14 lg:py-16"
  data-testid="home-question"
>
{/* Mamica levo */}
<img
  src="/icons/vd_mama_baby_question_transparent.png"
  alt=""
  aria-hidden="true"
  style={{
    position: 'absolute',
    left: '-70px',
    bottom: '60px',

    /* Slika ima veliko praznega prostora,
       zato jo namenoma močno povečamo */
    width: '900px',
    maxWidth: 'none',

    /* Nova ilustracija je že zelo nežna */
    opacity: 0.52,

    zIndex: 1,
    pointerEvents: 'none',

    WebkitMaskImage:
      'linear-gradient(to right, black 0%, black 48%, rgba(0,0,0,0.95) 58%, rgba(0,0,0,0.65) 72%, rgba(0,0,0,0.20) 87%, transparent 100%)',

    maskImage:
      'linear-gradient(to right, black 0%, black 48%, rgba(0,0,0,0.95) 58%, rgba(0,0,0,0.65) 72%, rgba(0,0,0,0.20) 87%, transparent 100%)',
  }}
/>

  <div className="relative z-10 w-full max-w-4xl mx-auto px-6 sm:px-8 lg:px-10 text-center">
    <p className="font-serif text-3xl sm:text-4xl text-vd-text leading-relaxed">
      Si se kdaj vprašala:{' '}
      <span className="italic text-vd-accent">
        »Ali to delam prav?«
      </span>
    </p>

    <p className="mt-4 font-serif text-2xl sm:text-3xl italic text-vd-text/90">
      Če si se, nisi edina.
    </p>

    <p className="mt-5 text-vd-muted leading-relaxed max-w-2xl mx-auto">
      Ne zato, ker bi bila slabša mamica. Ampak zato, ker je okoli tebe toliko različnih mnenj, da včasih vse težje zaupaš svojemu občutku.
    </p>

    <div className="mt-9">
      <button
        onClick={openPopup}
        className="inline-flex items-center gap-3 bg-vd-accent text-white px-8 py-4 rounded-full text-base hover:opacity-90 transition-all"
        data-testid="home-brochure-cta-primary"
      >
        Prejmi brezplačni priročnik
        <ArrowRightThin size={16} />
      </button>
    </div>

    <div className="mt-5">
      <button
        onClick={() => setBrochureExpanded(!brochureExpanded)}
        className="inline-flex items-center gap-2 text-vd-accent text-sm hover:opacity-70 transition-opacity"
        data-testid="home-more-toggle"
        aria-expanded={brochureExpanded}
      >
        Več o priročniku
        <ArrowRightThin
          size={12}
          className={`transition-transform ${
            brochureExpanded ? 'rotate-90' : ''
          }`}
        />
      </button>

      {brochureExpanded && (
        <div
          className="mt-6 vd-pencil-card p-6 sm:p-8 text-left space-y-4 vd-fade"
          data-testid="home-more-content"
        >
          <p className="text-vd-text/90 leading-relaxed">
            Ko sem v skupnosti mamic brala njihove zgodbe, sem opazila, da se vedno znova ponavljajo ista vprašanja.
          </p>

          <ul className="space-y-2 border-l-2 border-vd-primary pl-5 text-vd-secondary italic">
            <li>Ali to delam prav?</li>
            <li>Zakaj imam občutek, da mi ne gre?</li>
            <li>Sem edina, ki se tako počutim?</li>
          </ul>

          <p className="text-vd-text/90 leading-relaxed">
            Iz teh vprašanj je nastal brezplačni priročnik Val Dotika.
          </p>

          <p className="text-vd-text/90 leading-relaxed italic">
            Ker ima vsaka mamica svoje vprašanje.
          </p>

          <p className="text-vd-text/90 leading-relaxed">
            Skupaj s strokovnjakinjami, ki jim tudi sama zaupam, smo zbrale odgovore, ki jih v prvih tednih po porodu najbolj potrebuješ.
          </p>

          <div className="pt-3 text-center">
            <button
              onClick={openPopup}
              className="inline-flex items-center gap-2 bg-vd-accent text-white px-6 py-2.5 rounded-full text-sm hover:opacity-90 transition-all"
            >
              Prejmi brezplačni priročnik
              <ArrowRightThin size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  </div>

  {/* Valovi spodaj */}
  <svg
    aria-hidden="true"
    viewBox="0 0 1440 120"
    preserveAspectRatio="none"
    className="absolute bottom-0 left-0 w-full h-[95px] lg:h-[110px] pointer-events-none"
  >
    <path
      d="M-40 58 C140 22 280 104 465 61 C650 18 795 104 980 60 C1160 20 1300 96 1480 52"
      fill="none"
      stroke="rgba(133,119,94,0.24)"
      strokeWidth="1.15"
    />
    <path
      d="M-40 82 C145 45 300 120 485 80 C670 40 820 117 1000 78 C1180 40 1320 108 1480 75"
      fill="none"
      stroke="rgba(133,119,94,0.16)"
      strokeWidth="1"
    />
  </svg>
</section>

      {/* 10.6 Quote — soft olive citate (narrow) with beige watercolor smear */}
      <section className="py-8 lg:py-10 relative" data-testid="home-quote">

  <div className="relative max-w-xl mx-auto px-4 sm:px-6 lg:px-8">
    {/* Watercolor beige smear behind quote */}
    <span
      aria-hidden="true"
      className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[130%] pointer-events-none"
      style={{
        background:
          'radial-gradient(ellipse 65% 55% at 50% 50%, rgba(215, 195, 165, 0.28), rgba(215, 195, 165, 0.14) 55%, transparent 75%)',
        filter: 'blur(3px)',
      }}
    />

    <div className="relative px-6 py-4 sm:px-8 sm:py-5 text-center">
      <p className="font-serif text-2xl sm:text-[28px] text-vd-text leading-[1.42] italic">
        &ldquo;&nbsp;Ne zato, ker bi bila slabša mama. Ampak zato, ker je okoli nje toliko različnih mnenj, da vse težje zaupa svojemu <span className="whitespace-nowrap">občutku.&nbsp;&rdquo;</span>
      </p>
    </div>
  </div>
</section>

      {/* 10.7 How it all started */}
      <section className="py-12 lg:py-16 relative overflow-hidden" data-testid="home-story">
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-10 items-start">
          <div className="lg:col-span-2">
            <div className="relative w-48 h-48 sm:w-60 sm:h-60 lg:w-72 lg:h-72 mx-auto lg:mx-0" data-testid="home-story-avatar-wrapper">
              {/* Ornate hand-drawn SVG frame around Špela's photo — sketchy dashed loops */}
              <svg
  aria-hidden="true"
  viewBox="-25 -25 270 270"
  className="absolute inset-[-55px] w-[calc(100%+110px)] h-[calc(100%+110px)] pointer-events-none"
  style={{ overflow: 'visible' }}
>
  <defs>
    <filter
      id="pencil"
      x="-35%"
      y="-35%"
      width="170%"
      height="170%"
    >
                    <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="1" seed="4" />
                    <feDisplacementMap in="SourceGraphic" scale="1.2" />
                  </filter>
                </defs>
                <g
  fill="none"
  stroke="#8A7857"
  strokeLinecap="round"
  strokeLinejoin="round"
  filter="url(#pencil)"
>
  {/* bližnji notranji obroč */}
  <ellipse
    cx="110"
    cy="110"
    rx="101"
    ry="98"
    strokeWidth="1.05"
    opacity="0.36"
    transform="rotate(-3 110 110)"
  />

  {/* drugi obroč – malo zamaknjen desno/gor */}
  <ellipse
    cx="116"
    cy="104"
    rx="108"
    ry="102"
    strokeWidth="0.95"
    opacity="0.32"
    transform="rotate(7 116 104)"
  />

  {/* tretji – večji in zamaknjen levo/spodaj */}
  <ellipse
    cx="101"
    cy="116"
    rx="118"
    ry="106"
    strokeWidth="0.85"
    opacity="0.28"
    transform="rotate(-9 101 116)"
  />

  {/* četrti – precej večji */}
  <ellipse
    cx="120"
    cy="105"
    rx="126"
    ry="113"
    strokeWidth="0.75"
    opacity="0.22"
    transform="rotate(10 120 105)"
  />

  {/* največji zunanji obroč */}
  <ellipse
    cx="103"
    cy="111"
    rx="136"
    ry="119"
    strokeWidth="0.65"
    opacity="0.16"
    transform="rotate(-5 103 111)"
  />

  {/* dodatna odprta poteza zgoraj levo */}
  <path
    d="M-5 78 C16 38, 52 10, 95 3"
    strokeWidth="0.85"
    opacity="0.22"
  />

  {/* dodatna odprta poteza zgoraj desno */}
  <path
    d="M154 4 C199 27, 225 66, 228 109"
    strokeWidth="0.8"
    opacity="0.20"
  />

  {/* spodnja odprta poteza */}
  <path
    d="M10 190 C55 222, 118 228, 171 202"
    strokeWidth="0.75"
    opacity="0.18"
  />

  {/* desna spodnja poteza */}
  <path
    d="M185 181 C212 157, 226 129, 229 101"
    strokeWidth="0.7"
    opacity="0.16"
  />
</g>
              </svg>
              <div className="relative w-full h-full rounded-full overflow-hidden shadow-lg z-10">
                <img
                  src="/images/spela-ferbezar-ban-2026.jpg"
                  alt="Špela Ferbežar Ban"
                  className="w-full h-full object-cover scale-[1.02]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
          <div className="lg:col-span-3">
            <h2 className="font-serif text-3xl sm:text-4xl italic text-vd-text mb-6">Kako se je vse skupaj začelo</h2>
            <div className="space-y-4 text-vd-text/90 leading-relaxed">
              <p>Ko sem postala mama, sem imela občutek, da imajo vsi odgovor. Kaj je prav, kaj ni, kaj bi morala in kaj bi na mojem mestu naredili drugi.</p>
              <p>Počasi sem začela dvomiti vase. Ne zato, ker bi bila slaba mama, ampak ker je bilo okoli mene toliko različnih mnenj, da svojega glasu skoraj nisem več slišala.</p>
              <p>Takrat seveda nisem niti pomislila, da bo iz vsega tega nekoč nastal Val Dotika.</p>
              <p>Ampak leta pozneje sem začela poslušati zgodbe drugih mamic. In bolj ko sem jih poslušala, bolj sem si mislila: <span className="italic">˝Čakaj malo, saj se vse sprašujemo zelo podobne stvari.˝</span></p>
              <p>In nekje tam se je začel Val Dotika.</p>
            </div>
          </div>
        </div>
      </section>
{/* Skupna botanika čez spodnji del strani */}
<div className="relative overflow-hidden">

  {/* Nova botanika LEVO */}
  <img
    src="/icons/vd_botanical_mothers_left.png"
    alt=""
    aria-hidden="true"
    className="hidden md:block absolute left-[-8%] top-[1%] w-[42rem] lg:w-[52rem] opacity-[0.14] pointer-events-none select-none z-0"
  />

  {/* Nova botanika DESNO */}
  <img
    src="/icons/vd_botanical_mothers_right.png"
    alt=""
    aria-hidden="true"
    className="hidden md:block absolute right-[-9%] top-[5%] w-[42rem] lg:w-[54rem] opacity-[0.13] pointer-events-none select-none z-0"
  />


  {/* 10.8 Vision – Kaj želim ustvariti */}
  <section
    className="py-14 lg:py-20 relative z-10 min-h-[520px] flex items-center"
    data-testid="home-our-vision"
  >
    {/* Besedilo */}
    <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 className="font-serif text-3xl sm:text-4xl italic text-vd-text mb-8">
        Kaj želim ustvariti
      </h2>

      <div className="space-y-4 text-vd-text/90 leading-relaxed">
        <p>
          Ko sem začela graditi Val Dotika, si nisem želela ustvariti samo spletne strani.
          Želela sem ustvariti prostor, kamor se lahko vrneš, kadar potrebuješ podporo.
        </p>

        <p>
          Hitro sem ugotovila, da ti sama ne morem ponuditi vsega, kar lahko potrebuješ
          v prvih 40 dneh po porodu. In iskreno, niti nočem.
        </p>

        <p>
          Zato sem začela iskati strokovnjakinje, ki jim tudi sama zaupam.
          Vsako s svojim znanjem, vsako s svojim področjem.
        </p>

        <p>
          Da ti ne bi bilo treba iskati podpore na desetih različnih koncih,
          ampak bi jo lahko našla na enem mestu.
        </p>
      </div>
    </div>
  </section>


  {/* 10.9 Needs cards */}
  <section
    className="py-12 lg:py-16 relative z-10"
    data-testid="home-needs"
  >
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto text-center mb-8">
        <h2 className="font-serif text-3xl sm:text-4xl italic text-vd-text mb-4">
          Vsaka mamica potrebuje nekaj drugega
        </h2>

        <p className="text-vd-muted leading-relaxed">
          Zato boš tukaj našla različne strokovnjakinje. Namesto da začneš pri imenih, začni pri sebi. Kaj danes najbolj potrebuješ?
        </p>
      </div>

      <div
        className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5 reveal-stagger"
        ref={needsRef}
      >
        {NEEDS.map((n, i) => (
          <button
            key={i}
            onClick={() => navigate(n.to)}
            className="group text-left vd-need-card p-6"
            data-testid={`need-card-${i}`}
          >
            <IconImg
              src={n.icon}
              size={88}
              className="mb-4 opacity-90 group-hover:opacity-100 transition-opacity"
              alt=""
            />

            <p className="font-serif text-2xl sm:text-3xl italic text-vd-text leading-snug mb-4 font-semibold">
              {n.need}
            </p>

            <span className="inline-flex items-center gap-1.5 text-vd-accent text-sm group-hover:gap-2.5 transition-all">
              Poišči podporo <ArrowRightThin size={14} />
            </span>
          </button>
        ))}
      </div>
    </div>
  </section>
</div>
{/* Konec prvega botaničnega wrapperja */}
{/* Skupna botanika od testimonialov do zaključka strani */}
<div className="relative overflow-hidden pt-6 lg:pt-8">

  {/* Botanika LEVO */}
  <img
    src="/icons/vd_botanical_mothers_left.png"
    alt=""
    aria-hidden="true"
    className="hidden md:block absolute left-[-10%] top-[-3%] w-[46rem] lg:w-[56rem] opacity-[0.12] pointer-events-none select-none z-0"
  />

  {/* Botanika DESNO */}
  <img
    src="/icons/vd_botanical_mothers_right.png"
    alt=""
    aria-hidden="true"
    className="hidden md:block absolute right-[-10%] top-[-1%] w-[48rem] lg:w-[58rem] opacity-[0.11] pointer-events-none select-none z-0"
  />

  {/* 10.10 Testimonials */}
  <section
    className="py-12 lg:py-16 relative z-10"
    data-testid="home-testimonials"
  >
    <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 className="font-serif text-3xl sm:text-4xl italic text-vd-text text-center mb-8">
        Kako so našo zgodbo doživele druge mamice
      </h2>

      <div
        className="grid md:grid-cols-3 gap-5 reveal-stagger"
        ref={testimonialsRef}
      >
        {TESTIMONIALS.map((t, i) => (
          <article
            key={i}
            className="vd-testimonial-card p-6"
            data-testid={`testimonial-${i}`}
          >
            <p className="font-serif text-lg text-vd-text leading-relaxed mb-4 italic">
              &ldquo;{t.lead}&rdquo;
            </p>

            <button
              onClick={() => setOpenIdx(i)}
              className="text-vd-accent text-sm hover:opacity-70 transition-opacity"
              data-testid={`testimonial-open-${i}`}
            >
              Preberi celotno izkušnjo
            </button>
          </article>
        ))}
      </div>
    </div>

    {openIdx !== null && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center px-4"
        role="dialog"
        aria-modal="true"
        data-testid="testimonial-modal"
      >
        <div
          className="absolute inset-0 bg-vd-text/40 backdrop-blur-sm"
          onClick={() => setOpenIdx(null)}
        />

        <div className="relative bg-vd-bg rounded-2xl shadow-2xl max-w-lg w-full p-6 sm:p-8 border border-vd-soft vd-fade">
          <button
            onClick={() => setOpenIdx(null)}
            className="absolute top-3 right-3 text-vd-muted hover:text-vd-text p-2"
            aria-label="Zapri"
          >
            <CloseX size={18} />
          </button>

          <p className="text-vd-text leading-relaxed mb-5">
            {TESTIMONIALS[openIdx].full}
          </p>

          <p className="text-vd-secondary text-sm">
            {TESTIMONIALS[openIdx].author}
          </p>
        </div>
      </div>
    )}
  </section>

      {/* 10.11 Closing quote — with mama+baby silhouette + sage watercolor */}
      <section className="py-16 lg:py-24 relative z-10" data-testid="home-closing-quote">

<div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Sage green watercolor smear behind quote */}
          <span
            aria-hidden="true"
            className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[130%] pointer-events-none"
            style={{
              background:
                'radial-gradient(ellipse 65% 55% at 50% 50%, rgba(158, 168, 138, 0.28), rgba(158, 168, 138, 0.12) 55%, transparent 75%)',
              filter: 'blur(3px)',
            }}
          />
          <div className="relative px-6 py-7 sm:px-8 sm:py-8 text-center">
            <p className="font-serif text-3xl sm:text-[28px] text-vd-text leading-[1.42] italic">
  <span className="block sm:whitespace-nowrap">
    &ldquo; Vsaka zgodba je drugačna. Vsaka mamica
  </span>
  <span className="block sm:whitespace-nowrap">
    pa si zasluži občutek, da na tej poti ni sama. &rdquo;
  </span>
</p>
          </div>
        </div>
      </section>

      {/* Final closing + brochure inline */}
      <section className="py-12 lg:py-16 relative z-10" data-testid="home-closing">

{/* Wave motif removed — image had text baked in */}
        <div className="relative max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="font-serif text-2xl sm:text-3xl text-vd-text leading-relaxed">
            Če danes potrebuješ samo eno stvar, naj bo to občutek, da nisi sama.
          </p>
          <p className="mt-4 text-lg text-vd-muted leading-relaxed">
            Včasih je to najlepši začetek.
          </p>
          <p className="mt-6 font-serif text-3xl text-vd-accent italic">Dobrodošla.</p>

          <div className="mt-10 bg-white rounded-2xl border border-vd-soft p-6 sm:p-8 text-left" data-testid="home-brochure-inline">
            <BrochureForm variant="closing" />
          </div>
        </div>
            </section>

      {/* Konec spodnjega botaničnega wrapperja */}
      </div>

    </main>
  );
};

export default HomePage;
